import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  PaymentMeansEnum,
  paymentMeansEnumMapping
} from '../../../../lib/types/enums/payment-record/payment-means.enum'
import {
  StatusEnum,
  statusEnumMapping
} from '../../../../lib/types/enums/payment-record/status.enum'
import { PaymentRecordInput } from '../../../../lib/types/models/payment-record/payment-record-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { createDateStringSchema } from '../../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<PaymentRecordInput> & {
  mode: 'create' | 'edit'
}

const ENROLLMENT_PAYMENT_RECORD_SCHEMA: ZodType<PaymentRecordInput> = z.object({
  dateDue: createDateStringSchema({ inputName: "date d'échéance" }),
  dateEffective: createDateStringSchema({ inputName: 'date réalisée' }).optional(),
  amount: createNumberSchema('montant', { isPositive: true }),
  status: createEnumSchema({ enumType: StatusEnum, fieldName: 'statut', nullish: true }),
  means: createEnumSchema({
    enumType: PaymentMeansEnum,
    fieldName: 'moyen de paiement',
    nullish: true
  })
})

const EnrollmentPaymentRecordForm = ({ mode, defaultValues, ...validationButtonProps }: Props) => {
  const form = useForm<PaymentRecordInput>({
    resolver: zodResolver(ENROLLMENT_PAYMENT_RECORD_SCHEMA),
    defaultValues,
    mode: FORM_MODE
  })

  const statuses = useMemo(() => {
    if (mode === 'create') return [StatusEnum.PENDING, StatusEnum.SUCCEEDED]
    return Object.values(StatusEnum)
  }, [mode])

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className="flex flex-col gap-4 p-4"
      >
        <FormField
          control={form.control}
          name={'means'}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput<PaymentMeansEnum>
                  {...field}
                  options={Object.values(PaymentMeansEnum).map((means) => ({
                    label: paymentMeansEnumMapping[means],
                    value: means
                  }))}
                  label={'Moyen de paiement'}
                  selectedOption={getSelectedEnumOption(field.value, paymentMeansEnumMapping)}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'dateDue'}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledDateStringInput {...field} mode="over" label="Date d'échéance" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'dateEffective'}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledDateStringInput {...field} mode="over" label="Date réalisée" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={'amount'}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput {...field} type="number" label="Montant" />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'status'}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput<StatusEnum>
                  {...field}
                  options={statuses.map((status) => ({
                    label: statusEnumMapping[status].label,
                    value: status
                  }))}
                  label={'Statut'}
                  selectedOption={getSelectedEnumOption(field.value, statusEnumMapping)}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default EnrollmentPaymentRecordForm
